import React from "react";
import { Link } from "react-router-dom";
import "../css/main.scss";

const categoryTiles = [
  { image: "/main/m3-1.png", title: "SHOP HOMEWARES" },
  { image: "/main/m3-2.png", title: "SHOP NEW ARRIVALS" },
  { image: "/main/m3-3.png", title: "SHOP ACCESSORIES" },
  { image: "/main/m3-4.png", title: "SHOP FRAGRANCE & BODY" },
];

const collections = [
  "Lifestyle",
  "Home",
  "Fashion",
  "Fragrance",
  "Accessories",
  "New Arrivals",
  "Gifts",
  "Sale",
].map((title, index) => ({ title, image: `/main/m5-${index + 1}.png` }));

const socialImages = [1, 2, 3, 4].map((item) => `/main/m6-${item}.png`);

const editorialTiles = [
  {
    image: "/main/m7-1.png",
    eyebrow: "HOME & BODY",
    title: "Everyday rituals, beautifully considered",
  },
  {
    image: "/main/m7-2.png",
    eyebrow: "THE EDIT",
    title: "Easy pieces for every occasion",
  },
  {
    image: "/main/m7-3.png",
    eyebrow: "OFF-DUTY",
    title: "Comfort, with a little more style",
  },
];

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14M14 7l5 5-5 5" />
    </svg>
  );
}

function BenefitIcon({ type }) {
  const icons = {
    shipping: (
      <>
        <path d="M3 6h11v10H3zM14 10h4l3 3v3h-7z" />
        <circle cx="7" cy="18" r="2" />
        <circle cx="18" cy="18" r="2" />
      </>
    ),
    offer: (
      <>
        <path d="M4 8h16v12H4zM8 8a4 4 0 0 1 8 0" />
        <path d="M12 12v4M10 14h4" />
      </>
    ),
    secure: (
      <>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18M7 14h3" />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {icons[type]}
    </svg>
  );
}

function Main() {
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="main">
      <main>
        <section className="main-hero" aria-labelledby="hero-title">
          <img src="/main/m1.png" alt="Cewillow Market everyday fashion edit" />
          <div className="main-hero__shade" />
          <div className="main-hero__content">
            <span>THE EVERYDAY EDIT</span>
            <h1 id="hero-title">
              STYLE FOR
              <br />
              EVERY DAY
            </h1>
            <Link
              className="main-button main-button--light"
              to="/shop"
              onClick={toTop}
            >
              Shop sale
            </Link>
          </div>
        </section>

        <section className="main-feature" aria-labelledby="feature-title">
          <div className="main-feature__image">
            <img
              src="/main/m2.png"
              alt="Model wearing statement earrings"
              loading="lazy"
            />
          </div>
          <div className="main-feature__content">
            <span className="main-kicker">THE FINISHING TOUCH</span>
            <h2 id="feature-title">REFINE YOUR OUTFIT</h2>
            <p>
              The finishing touches that bring everything together. Simple,
              considered pieces designed to complement your everyday wardrobe.
            </p>
            <Link className="main-text-link" to="/shop" onClick={toTop}>
              Shop accessories <ArrowIcon />
            </Link>
          </div>
        </section>

        <section
          className="main-categories"
          aria-label="Shop featured categories"
        >
          {categoryTiles.map((tile) => (
            <Link
              className="main-category"
              to="/shop"
              onClick={toTop}
              key={tile.title}
            >
              <img src={tile.image} alt="" loading="lazy" />
              <span className="main-category__shade" />
              <span className="main-category__content">
                <strong>{tile.title}</strong>
                <small>
                  Shop now <ArrowIcon />
                </small>
              </span>
            </Link>
          ))}
        </section>

        <section className="main-benefits" aria-label="Shopping benefits">
          <article>
            <BenefitIcon type="shipping" />
            <div>
              <h3>Simple delivery</h3>
              <p>Flat-rate shipping for $9.99</p>
            </div>
          </article>
          <article>
            <BenefitIcon type="offer" />
            <div>
              <h3>Thoughtfully selected</h3>
              <p>Useful pieces chosen for quality and everyday appeal.</p>
            </div>
          </article>
          <article>
            <BenefitIcon type="secure" />
            <div>
              <h3>Secure checkout</h3>
              <p>Complete payment through our protected payment page.</p>
            </div>
          </article>
        </section>

        <section className="main-story" aria-labelledby="story-title">
          <div className="main-story__content">
            <span className="main-kicker">ABOUT US</span>
            <h2 id="story-title">OUR STORY</h2>
            <p>
              Cewillow Market brings together easy fashion, useful accessories,
              home details and thoughtful gifts. We focus on practical pieces
              with quiet character, selected to fit naturally into everyday life.
            </p>
            <Link className="main-text-link" to="/about" onClick={toTop}>
              Read more <ArrowIcon />
            </Link>
          </div>
          <div className="main-story__image">
            <img
              src="/main/m4.png"
              alt="Cewillow Market home and lifestyle collection"
              loading="lazy"
            />
          </div>
        </section>

        <section className="main-collection" aria-labelledby="collection-title">
          <div className="main-section-heading">
            <span className="main-kicker">EXPLORE</span>
            <h2 id="collection-title">SHOP BY COLLECTION</h2>
          </div>
          <div className="main-collection__scroller">
            {collections.map((item) => (
              <Link
                to="/shop"
                onClick={toTop}
                className="main-collection-card"
                key={item.title}
              >
                <div>
                  <img src={item.image} alt={item.title} loading="lazy" />
                </div>
                <span>{item.title}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="main-social" aria-labelledby="social-title">
          <div className="main-section-heading">
            <span className="main-kicker">THE DETAILS</span>
            <h2 id="social-title">CEWILLOW MARKET INSPIRATION</h2>
          </div>
          <div className="main-social__grid">
            {socialImages.map((image, index) => (
              <Link
                to="/shop"
                onClick={toTop}
                key={image}
              >
                <img
                  src={image}
                  alt={`Cewillow Market styled collection ${index + 1}`}
                  loading="lazy"
                />
                <span aria-hidden="true">↗</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="main-editorial" aria-label="Featured edits">
          {editorialTiles.map((item) => (
            <Link
              to="/shop"
              onClick={toTop}
              className="main-editorial-card"
              key={item.title}
            >
              <img src={item.image} alt="" loading="lazy" />
              <span className="main-editorial-card__shade" />
              <span className="main-editorial-card__content">
                <small>{item.eyebrow}</small>
                <strong>{item.title}</strong>
                <em>Discover now</em>
              </span>
            </Link>
          ))}
        </section>
      </main>
    </div>
  );
}

export default Main;
